import React from 'react';
import styled, { css } from 'styled-components';
import { em } from 'polished';
import uid from 'utils/uid';
import tulkojumi from 'utils/tulkojumi';
import {
  colors,
  elevation,
  accessibilityBorder,
  getThemeModes,
  DEAULT_THEME_MODE
} from 'core/theme';
import { media } from 'core/layout';
import { Contrast } from 'core/icons';
import { ButtonStylerGrey } from 'elements/Button/styled';
import { Button } from 'elements';


const Wrapper = styled.div`
  position: relative;
`;

const ListDrop = styled.ul`
  list-style: none;
  margin: 0;
  padding-left: 0;

  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(100%);
  z-index: 100;

  background-color: ${colors.background};
  border-radius: 2px;
  box-shadow: ${elevation.Z2};
  display: ${props => props.open ? 'block' : 'none'};
  padding: ${em(16)};
`;

const ModeIcon = styled.span`
  background-color: ${props => colors.lightThemed[props.themeId]};
  border-radius: 50%;
  border: 1px solid transparent;
  color: ${props => colors.darkThemed[props.themeId]};
  font-size: ${em(18)};
  display: block;
  width: ${em(32)};
  height: ${em(32)};
  line-height: ${em(32)};
  ${({ themeId }) => themeId === DEAULT_THEME_MODE && css`
    border: 1px solid #EDEDED;
  `};
  ${accessibilityBorder};
`;

const MenuItem = styled.li`
  &[aria-selected="true"] {
    button {}
  }

  & + & {
    margin-top: ${em(4)};
  }

  button:hover {
    ${ModeIcon} {
      border: 1px solid ${props => colors.darkThemed[props.themeId]};
    }
  }
`;

const InlineLayout = styled.div`
  display: flex;
  margin-top: 1em;

  padding-left: 60px;
  ${media.tablet`
    padding-left: 32px;
  `};

  ${media.tabletSmall`
    padding-left: 20px;
  `};

  & > p {
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 0 0 auto;
    width: 38px;
  }

  & > ul {
    list-style: none;
    display: flex;
    margin: 0;
    padding-left: 1em;

    flex: 1 1 auto;
    flex-wrap: wrap;

    & > li {
      margin-top: 0 !important;
    }

    button {
      min-width: 62px;
    }
  }
`;

class ThemeModeMenu extends React.PureComponent {
  state = {
    open: false,
    selectedOption: undefined,
  };

  componentDidMount() {
    if (window) {
      this._uid = uid();

      let selectedOptionId;
      const modes = getThemeModes();
      Object.keys(modes).find((key, i) => {
        if (key === DEAULT_THEME_MODE) {
          selectedOptionId = `${this._uid}-${i + 1}`;
        }
        return false;
      });

      if (selectedOptionId !== this.state.selectedOption) {
        this.setState(prevState => Object.assign({}, prevState, {
          selectedOption: selectedOptionId,
        }))
      }

      window.addEventListener('click', this._onWindowClick);
    }
  }

  _onMenuClick = (event) => {
    if (!this.state.open) {
      this.setState(prevState => Object.assign({}, prevState, {
        open: true,
      }), () => {
        // NOTE: Novietojam fokusu uz izvēlēto elementu uzreiz pēc menu atvēršanas
        const selectedOption = this._menu.querySelector('li[aria-selected="true"]');
        if (selectedOption) {
          const button = selectedOption.querySelector('button');
          button.focus();
        }
      });
    }
  }

  _onWindowClick = (event) => {
    if (event.target.id === this._uid) {
      event.stopImmediatePropagation();
      return;
    }
    if (this.state.open) {
      this.setState(prevState => Object.assign({}, prevState, {
        open: false,
      }));
    }
  }

  _onChanged = (globalId, id) => {
    if (this.state.selectedOption !== globalId) {
      this.setState(prevState => Object.assign({}, prevState, {
        selectedOption: globalId,
      }), () => {
        const { onChange } = this.props;
        onChange && onChange(id);
      });
    }
  }

  // FIXME: SSR procesā tiek uzģenerēts _uid un pievienots pogai;
  // klienta pusē atkal tiek ģenerēts jauns _uid, bet render nenostrādā,
  // tāpēc DOM elements paliek ar _uid, kas bijis ģenerēts servera pusē.

  render() {
    const { teksti, inline = false } = this.props;

    const modes = getThemeModes();
    const options = Object.keys(modes).map((id, i) => {
      const mode = modes[id];
      let index = i + 1;
      const globalId = `${this._uid}-${index}`;
      // Izvēlēto režīmu sarakstā nerādam
      if (this.state.selectedOption && this.state.selectedOption === globalId) {
        index -= 1;
        return null;
      }
      return (
        <MenuItem
          key={globalId}
          id={globalId}
          role='option'
          aria-selected={false}
        >
          <ButtonStylerGrey>
            <Button
              tabIndex={this.state.open ? '0' : '-1'}
              type='text'
              title={mode}
              // a11yLabel={`${tulkojumi(teksti, 'rezims_vajredzigajiem')} – ${mode}`}
              title={`${tulkojumi(teksti, 'rezims_vajredzigajiem')} – ${mode}`}
              onClick={() => {
                this._onChanged(globalId, id);
              }}
            >
              <ModeIcon themeId={id}>
                A
              </ModeIcon>
            </Button>
          </ButtonStylerGrey>
        </MenuItem>
      );
    });

    if (inline) {
      return (
        <InlineLayout>
          <p title={tulkojumi(teksti, 'rezims_vajredzigajiem')}>
            <Contrast />
          </p>
          <ul>
            {options}
          </ul>
        </InlineLayout>
      );
    }

    return (
      <Wrapper
        role='listbox'
      >
        <ButtonStylerGrey>
          <Button
            type='text'
            id={this._uid}
            icon={Contrast}
            // a11yLabel={tulkojumi(teksti, 'rezims_vajredzigajiem')}
            title={tulkojumi(teksti, 'rezims_vajredzigajiem')}
            onClick={this._onMenuClick}
          >

          </Button>
        </ButtonStylerGrey>

        <ListDrop
          innerRef={c => this._menu = c}
          open={this.state.open}
        >
          { options }
        </ListDrop>
      </Wrapper>
    );
  }
}

export default ThemeModeMenu;



// WEBPACK FOOTER //
// ./src/modules/Header/components/ThemeModeMenu/ThemeModeMenu.jsx